import { Link, createFileRoute, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
    AlertCircle,
    MapPinned,
    NotebookPen,
    Sprout,
    UserRound,
    WifiOff,
} from 'lucide-react'

import { fetchPublicGrowerArea } from '@/lib/api/public-grower-area-api'
import { GrowerAreaMap } from '@/components/growers/area-map/grower-area-map'
import { GoogleMapsProvider } from '@/components/maps/google-maps-provider'
import { useAuth } from '@/hooks/use-auth'
import { useCareLogAreaContext } from '@/hooks/use-care-logs-query'
import { useGrowerAreaGrowerLink } from '@/hooks/use-growers-query'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

export const Route = createFileRoute('/grower-areas/$id')({
    component: GrowerAreaPublicPage,
})

/**
 * Trang thông tin vùng trồng CÔNG KHAI — đích đến của mã QR vùng trồng.
 *
 * Không cần đăng nhập: người quét là khách mua chè, không có tài khoản. Vì thế
 * trang chỉ hiển thị mã vùng, tên vùng, diện tích, hình thửa trên bản đồ và TÊN
 * KHAI SINH của hộ; cố ý không có tên thường gọi, số điện thoại, địa chỉ, CCCD
 * hay tài khoản ngân hàng. API công khai cũng không trả những trường đó — trang
 * này chỉ đọc đúng các trường an toàn, nên API lỡ trả thừa cũng không lộ ra.
 */
export function GrowerAreaPublicPage() {
    const { id } = useParams({ from: '/grower-areas/$id' })

    const { data, isPending, isError } = useQuery({
        queryKey: ['public-grower-area', id],
        queryFn: () => fetchPublicGrowerArea(id),
        retry: false,
    })

    // Nút "Tạo nhật ký" chỉ dành cho tài khoản được ghi nhật ký cho CHÍNH hộ sở
    // hữu vùng này — server quyết (cùng luật phạm vi với tạo nhật ký). Khách
    // quét QR thì không hỏi gì: request cần đăng nhập chỉ sinh 401 vô ích.
    const { user, hasPermission } = useAuth()
    const canWriteCareLogs = !!user && hasPermission('care_logs.write')
    const { data: careLogArea } = useCareLogAreaContext(
        canWriteCareLogs ? id : null,
    )
    // Nút "Xem chi tiết hộ": growers.read xem mọi hộ, care_logs.read chỉ hộ gắn
    // với tài khoản — server quyết và nói mở màn nào. Không có quyền nào trong
    // hai thì hỏi cũng chỉ nhận 404.
    const canViewGrowers =
        !!user &&
        (hasPermission('growers.read') || hasPermission('care_logs.read'))
    const { data: growerLink } = useGrowerAreaGrowerLink(
        canViewGrowers ? id : null,
    )

    if (isPending) {
        return (
            <Screen>
                <p className="animate-pulse text-sm text-muted-foreground">
                    {m.grower_area_public_loading()}
                </p>
            </Screen>
        )
    }

    // Hỏng mạng và "không có vùng này" là hai chuyện khác nhau: gộp lại là bảo
    // khách rằng vùng không tồn tại trong khi họ chỉ đang mất sóng.
    if (isError) {
        return (
            <Screen>
                <StatusBlock
                    icon={<WifiOff className="size-12 text-muted-foreground" />}
                    title={m.grower_area_public_load_error_title()}
                    desc={m.grower_area_public_load_error_desc()}
                />
            </Screen>
        )
    }

    if (!data) {
        return (
            <Screen>
                <StatusBlock
                    icon={<AlertCircle className="size-12 text-destructive" />}
                    title={m.grower_area_public_not_found_title()}
                    desc={m.grower_area_public_not_found_desc()}
                />
            </Screen>
        )
    }

    const paths = data.paths ?? []

    return (
        <main className="min-h-screen bg-slate-50 px-4 py-8">
            <div className="mx-auto max-w-lg space-y-6">
                <header className="space-y-1 text-center">
                    <Sprout className="mx-auto size-8 text-green-700" />
                    <h1 className="text-2xl font-bold text-green-800">
                        {m.grower_area_public_title()}
                    </h1>
                </header>

                <Card>
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Row
                            label={m.grower_area_public_code()}
                            value={data.code}
                            mono
                        />
                        <Row
                            label={m.grower_area_public_name()}
                            value={data.name}
                        />
                        <Row
                            label={m.grower_area_public_acreage()}
                            value={acreageText(data.acreage)}
                        />
                        <Row
                            label={m.grower_area_public_grower()}
                            value={data.grower_name}
                        />
                    </dl>
                </Card>

                {careLogArea ? (
                    <Link
                        to="/agriculture/care-logs/new"
                        search={{ area_id: careLogArea.area_id }}
                        className={twJoin(
                            'flex h-12 w-full items-center justify-center gap-2 rounded-xl',
                            'bg-green-700 font-semibold text-white shadow-sm',
                            'transition-colors hover:bg-green-800 active:bg-green-900',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2',
                        )}
                    >
                        <NotebookPen className="size-5" />
                        {m.grower_area_public_create_care_log()}
                    </Link>
                ) : null}

                {growerLink ? (
                    <GrowerDetailLink
                        growerId={growerLink.grower_id}
                        detail={growerLink.detail}
                    />
                ) : null}

                <Card
                    title={m.grower_area_public_map()}
                    icon={<MapPinned className="size-4" />}
                >
                    {paths.length > 0 ? (
                        // `neighbors={false}`: lớp thửa xung quanh đọc API cần
                        // đăng nhập, bật lên ở đây là trang công khai bắn ra một
                        // loạt request 401 rồi vẫn không vẽ được gì.
                        <GoogleMapsProvider>
                            <GrowerAreaMap
                                value={{ paths }}
                                onChange={() => {}}
                                readOnly
                                fitKey={data.area_id}
                                neighbors={false}
                            />
                        </GoogleMapsProvider>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            {m.grower_area_public_no_paths()}
                        </p>
                    )}
                </Card>

                <p className="pb-4 text-center text-xs text-muted-foreground">
                    {m.grower_area_public_footer()}
                </p>
            </div>
        </main>
    )
}

const viewGrowerLinkClass = twJoin(
    'flex h-12 w-full items-center justify-center gap-2 rounded-xl',
    'border border-green-700 bg-white font-semibold text-green-800 shadow-sm',
    'transition-colors hover:bg-green-50 active:bg-green-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2',
)

/** Nút mở màn chi tiết hộ. `full` → hồ sơ hộ quản trị; `own` → "hộ của tôi"
 *  (tài khoản hộ không có growers.read nên không vào được màn quản trị). */
function GrowerDetailLink({
    growerId,
    detail,
}: {
    growerId: string
    detail: 'full' | 'own'
}) {
    const content = (
        <>
            <UserRound className="size-5" />
            {m.grower_area_public_view_grower()}
        </>
    )
    return detail === 'full' ? (
        <Link
            to="/agriculture/growers/$id"
            params={{ id: growerId }}
            className={viewGrowerLinkClass}
        >
            {content}
        </Link>
    ) : (
        <Link
            to="/agriculture/my-grower/$growerId"
            params={{ growerId }}
            className={viewGrowerLinkClass}
        >
            {content}
        </Link>
    )
}

/** Diện tích lưu theo m² (xem màn hộ trồng), hiện theo cách đọc số của người
 *  Việt: 1.200 m². */
function acreageText(acreage: number): string {
    return `${(acreage || 0).toLocaleString('vi-VN')} m²`
}

function Screen({ children }: { children: React.ReactNode }) {
    return (
        <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4">
            {children}
        </main>
    )
}

function StatusBlock({
    icon,
    title,
    desc,
}: {
    icon: React.ReactNode
    title: string
    desc: string
}) {
    return (
        <>
            {icon}
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="max-w-xs text-center text-muted-foreground">{desc}</p>
        </>
    )
}

function Card({
    title,
    icon,
    children,
}: {
    title?: string
    icon?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <section className="space-y-3 rounded-xl bg-white p-4 shadow-sm">
            {title && (
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-green-800">
                    {icon}
                    <span>{title}</span>
                </h2>
            )}
            {children}
        </section>
    )
}

function Row({
    label,
    value,
    mono,
}: {
    label: string
    value: string
    mono?: boolean
}) {
    return (
        <div>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className={mono ? 'font-mono font-medium' : 'font-medium'}>
                {value}
            </dd>
        </div>
    )
}
